import * as React from "react";
import { Compass, Route } from "lucide-react";
import {
  SegmentedControl,
  SegmentedControlItem,
} from "@/components/ui/segmented-control";
import { cn } from "@/lib/utils";

export type DemoMode = "tour" | "free";

const OPTIONS: {
  value: DemoMode;
  label: string;
  caption: string;
  Icon: typeof Route;
}[] = [
  {
    value: "tour",
    label: "Интерактивный тур",
    caption: "Пошаговое знакомство с основными возможностями сервиса",
    Icon: Route,
  },
  {
    value: "free",
    label: "Свободное демо",
    caption: "Самостоятельная работа без подсказок и ограничений",
    Icon: Compass,
  },
];

const expandLabel =
  "group-hover/demo:max-w-40 group-hover/demo:opacity-100 group-has-[:focus-visible]/demo:max-w-40 group-has-[:focus-visible]/demo:opacity-100";
const expandPad =
  "group-hover/demo:gap-1.5 group-hover/demo:px-2.5 group-has-[:focus-visible]/demo:gap-1.5 group-has-[:focus-visible]/demo:px-2.5";
const expandedLabel = "max-w-40 opacity-100";
const expandedPad = "gap-1.5 px-2.5";

/** Keep labels painted through brief pointer gaps (tour shield hole, layout). */
const HOVER_COLLAPSE_DELAY_MS = 220;

export type DemoModeSwitcherProps = {
  value: DemoMode;
  onValueChange: (mode: DemoMode) => void;
  variant?: "welcome" | "header";
  /** Keep segment labels visible (in-flow; welcome / explicit callers). */
  labelsAlwaysVisible?: boolean;
  /** When false, header stays icon-only — click still switches mode. */
  expandOnHover?: boolean;
  className?: string;
};

export function DemoModeSwitcher({
  value,
  onValueChange,
  variant = "header",
  labelsAlwaysVisible = false,
  expandOnHover = true,
  className,
}: DemoModeSwitcherProps) {
  const isWelcome = variant === "welcome";
  const overlayExpand = !isWelcome && !labelsAlwaysVisible && expandOnHover;
  const [hoverPinned, setHoverPinned] = React.useState(false);
  const collapseTimerRef = React.useRef<number | null>(null);

  React.useEffect(
    () => () => {
      if (collapseTimerRef.current != null) {
        window.clearTimeout(collapseTimerRef.current);
      }
    },
    [],
  );

  React.useEffect(() => {
    if (overlayExpand) return;
    if (collapseTimerRef.current != null) {
      window.clearTimeout(collapseTimerRef.current);
      collapseTimerRef.current = null;
    }
    setHoverPinned(false);
  }, [overlayExpand]);

  const pinHover = React.useCallback(() => {
    if (collapseTimerRef.current != null) {
      window.clearTimeout(collapseTimerRef.current);
      collapseTimerRef.current = null;
    }
    setHoverPinned(true);
  }, []);

  const unpinHover = React.useCallback(() => {
    if (collapseTimerRef.current != null) {
      window.clearTimeout(collapseTimerRef.current);
    }
    collapseTimerRef.current = window.setTimeout(() => {
      collapseTimerRef.current = null;
      setHoverPinned(false);
    }, HOVER_COLLAPSE_DELAY_MS);
  }, []);

  const forceExpanded = labelsAlwaysVisible || hoverPinned;

  return (
    <div
      data-tour-passthrough="demo-mode"
      data-expanded={forceExpanded ? "true" : "false"}
      onPointerEnter={overlayExpand ? pinHover : undefined}
      onPointerLeave={overlayExpand ? unpinHover : undefined}
      className={cn(
        "flex flex-col",
        isWelcome
          ? "w-full max-w-xl items-stretch gap-2"
          : overlayExpand
            ? "relative z-20 h-9 shrink-0"
            : "items-center",
        className,
      )}
    >
      {overlayExpand ? (
        <div
          aria-hidden
          className="pointer-events-none flex h-9 select-none overflow-hidden rounded-m border border-transparent opacity-0"
        >
          {OPTIONS.map((option) => (
            <span key={option.value} className="flex h-full items-center px-2.5">
              <option.Icon className="size-4 shrink-0" />
            </span>
          ))}
        </div>
      ) : null}
      <SegmentedControl
        type="single"
        value={value}
        onValueChange={(next) => {
          if (next === "tour" || next === "free") onValueChange(next);
        }}
        aria-label="Режим демо"
        className={cn(
          // Clip selected fill to the track outline — no nested radius mismatch.
          "group/demo gap-0 overflow-hidden rounded-m border border-neutral-border-300 bg-neutral-background-100 p-0",
          "transition-[border-color,box-shadow] duration-200 ease-in-out motion-reduce:transition-none",
          "hover:border-primary-500/40 hover:ring-2 hover:ring-primary-500/12",
          isWelcome ? "w-full" : "h-9 shrink-0 items-stretch",
          overlayExpand && "absolute top-0 right-0 z-20",
          overlayExpand && forceExpanded && "shadow-m",
        )}
      >
        {OPTIONS.map((option) => {
          const isTour = option.value === "tour";
          return (
            <SegmentedControlItem
              key={option.value}
              value={option.value}
              title={option.label}
              className={cn(
                "flex items-center overflow-hidden whitespace-nowrap rounded-none font-medium",
                "transition-[color,background-color,gap,padding] duration-200 ease-in-out motion-reduce:transition-none",
                "text-neutral-text-500 hover:text-neutral-text-800",
                isWelcome
                  ? "flex-1 justify-center gap-1.5 px-3 py-2 text-body2"
                  : cn(
                      "h-full gap-0 px-2.5 py-0 text-caption",
                      forceExpanded ? expandedPad : expandOnHover ? expandPad : undefined,
                    ),
                isTour
                  ? "data-[state=on]:bg-primary-500 data-[state=on]:text-neutral-white"
                  : "data-[state=on]:bg-neutral-background-200 data-[state=on]:text-neutral-text-900",
              )}
            >
              <option.Icon className="size-4 shrink-0" aria-hidden />
              <span
                className={cn(
                  isWelcome
                    ? undefined
                    : cn(
                        "max-w-0 overflow-hidden opacity-0",
                        "transition-[max-width,opacity] duration-200 ease-in-out motion-reduce:transition-none",
                        forceExpanded
                          ? expandedLabel
                          : expandOnHover
                            ? expandLabel
                            : undefined,
                      ),
                )}
              >
                {option.label}
              </span>
            </SegmentedControlItem>
          );
        })}
      </SegmentedControl>
      {isWelcome ? (
        <div className="grid grid-cols-2 gap-1 px-1">
          {OPTIONS.map((option) => (
            <p
              key={option.value}
              className="text-center text-caption text-neutral-text-500"
            >
              {option.caption}
            </p>
          ))}
        </div>
      ) : null}
    </div>
  );
}
